import { type JSX, useId, type ComponentProps, type ReactNode } from 'react'

import { Label } from '../components/ui/label.tsx'
import { cn } from '../lib/cn.ts'
import { Icon } from './icon.tsx'

/** One choice in a `Select`. */
export interface SelectOption {
  value: string
  label: string
}

export type SelectProps = Omit<ComponentProps<'select'>, 'children'> & {
  'data-testid': string
  label: ReactNode
  options: readonly SelectOption[]
  /** Adds an empty first choice with this text. Absent means the list is the whole choice. */
  placeholder?: string
  /** A message means invalid; it also sets aria-invalid. */
  error?: string
  hint?: ReactNode
}

/**
 * Select — a labelled choice from a fixed list.
 *
 * A native `select`, not a Radix combobox. The reasons live in
 * `docs/decisions/0006-the-console-select-is-native.md`. Shaped like `Input`
 * on purpose — same id handling, same single message slot where the error
 * wins, same required `data-testid`. Two controls that look alike on the page
 * should not be two different props contracts in the source.
 *
 * @param props - Select props.
 * @param props.label - Field label, rendered above the control.
 * @param props.options - The choices, in the order they are shown.
 * @param props.placeholder - Text for an empty leading choice.
 * @param props.error - Error message; sets aria-invalid.
 * @param props.hint - Shown when there is no error.
 * @param props.id - Overrides the generated id.
 * @returns The labelled select.
 */
/**
 * The native select, drawn as the pickers draw their trigger.
 *
 * The merchant and reason pickers are outline buttons with a chevron, and a
 * native select beside them drew white with the browser's own arrow. This
 * keeps the native element, for its keyboard and its phone wheel, and draws
 * it in the outline button's fill, edge and shadow with the same chevron.
 */
export const CHOOSER_CLASS = cn(
  'border-input bg-background h-9 w-full appearance-none rounded-md border pr-8 pl-3 text-sm shadow-xs',
  'hover:bg-accent hover:text-accent-foreground',
  'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none',
  'disabled:cursor-not-allowed disabled:opacity-50'
)

/**
 * The chevron over a chooser's right end. The select under it still takes
 * the click, because the glyph passes pointer events through.
 *
 * @returns The chevron.
 */
export function ChooserChevron(): JSX.Element {
  return (
    <Icon
      name="chevrons-up-down"
      className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 opacity-50"
    />
  )
}

export function Select({
  label,
  options,
  placeholder,
  error,
  hint,
  id,
  className,
  'data-testid': testId,
  ...props
}: SelectProps): JSX.Element {
  const generated = useId()
  const fieldId = id ?? generated
  const messageId = `${fieldId}-message`
  const message = error ?? hint

  return (
    <div className="grid gap-2">
      <Label htmlFor={fieldId}>{label}</Label>
      <div className="relative">
        <select
          id={fieldId}
          data-testid={testId}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          className={cn(
            CHOOSER_CLASS,
            error && 'border-destructive',
            className
          )}
          {...props}
        >
          {placeholder === undefined ? null : (
            <option value="">{placeholder}</option>
          )}
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChooserChevron />
      </div>
      {message ? (
        <p
          id={messageId}
          className={cn(
            'text-sm',
            error ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {message}
        </p>
      ) : null}
    </div>
  )
}
